import React, { useRef, useEffect, useState } from "react";
import "../../../../styles/useCustomScrollbar";
import { calculateCPFRatio, formatCPFRatio } from "../../CPFCalculator";
import { sendMealPlanToClient } from "../../../../../services/mealPlans";
import useCustomScrollbar from "../../../../styles/useCustomScrollbar";
import { Button } from "../../../../Ui_Components/Button";

// ─── Types (mirrored from MealPlanDetailsPage) ───────────────────────────────

export interface DishItem {
  id: string;
  name: string;
  quantity: string;
  unit: string;
  oil: number;
  cal: number;
  gl: number;
  carb: number;
  protein: number;
  fat: number;
}

export interface MealOption {
  id: string;
  name: string;
  mealTimingId: string;
  dishes: DishItem[];
  instructions?: string;
  totals: {
    cal: number;
    carb: number;
    fat: number;
    protein: number;
    gl: number;
  };
  isNewlyCreated?: boolean;
  optionNumber?: number;
}

export interface MealTiming {
  id: string;
  mealType: string;
  time: { hour: string; minute: string; period: string };
  fk_user_meal: number;
}

export interface NutritionData {
  total_cals_max: number;
  total_cals_min: number;
  cals_units: string;
  fat_max: number;
  fat_min: number;
  fat_units: string;
  carbs_max: number;
  carbs_min: number;
  carbs_units: string;
  protein_max: number;
  protein_min: number;
  protein_units: string;
  GL_max: number;
  GL_min: number;
  GL_units: string;
  CPF_max: string;
  CPF_min: string;
  current_cal: number;
  current_protein: number;
  current_carbs: number;
  current_fats: number;
  current_gl: number;
  current_CPF: string;
}

// ─── CPF helper function using the custom calculator ────────────────────────

function calcCPF(
  carb: number,
  protein: number,
  fat: number,
  calories: number,
): string {
  const result = calculateCPFRatio(carb, protein, fat, calories);
  return formatCPFRatio(result.ratio);
}

// ─── Expandable Option Row ───────────────────────────────────────────────────

const OptionRow: React.FC<{ option: MealOption }> = ({ option }) => {
  const [expanded, setExpanded] = useState(false);
  const filledDishes = option.dishes.filter((d) => d.name);
  const cpf = calcCPF(
    option.totals.carb,
    option.totals.protein,
    option.totals.fat,
    option.totals.cal,
  );

  return (
    <>
      <tr
        className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA] transition-colors cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="pt-4 pb-2.5 pl-8 pr-3">
          <div className="flex items-center gap-2">
            <button
              className="text-[#8E8E93] hover:text-[#0DB5B5] transition-colors flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                className={`transition-transform duration-200 ${expanded ? "rotate-90" : ""}`}
              >
                <path
                  d="M5 3l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <span className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
              {option.name}
            </span>
            <span className="text-[11px] text-[#AEAEB2]">
              ({filledDishes.length} dishes)
            </span>
          </div>
        </td>
        <td className="pt-4 pb-2.5 px-3 text-sm text-[#1F1F1F] text-right">
          {Math.round(option.totals.cal)}
        </td>
        <td className="pt-4 pb-2.5 px-3 text-sm text-[#1F1F1F] text-right">
          {Math.round(option.totals.carb)}g
        </td>
        <td className="pt-4 pb-2.5 px-3 text-sm text-[#1F1F1F] text-right">
          {Math.round(option.totals.protein)}g
        </td>
        <td className="pt-4 pb-2.5 px-3 text-sm text-[#1F1F1F] text-right">
          {Math.round(option.totals.gl)}g
        </td>
        <td className="pt-4 pb-2.5 px-3 text-sm text-[#1F1F1F] text-right">
          {Math.round(option.totals.fat)}g
        </td>
        <td className="pt-4 pb-2.5 pl-3 pr-4 text-sm text-[#1F1F1F] text-right">
          {cpf}
        </td>
      </tr>

      {/* Expanded dishes */}
      {expanded && (
        <tr className="bg-white">
          <td colSpan={7} className="px-0 py-0">
            <div className="pl-16 pr-4 py-3">
              {/* Dish sub-table header */}
              <div className="grid grid-cols-[2.8fr_0.6fr_0.3fr_0.9fr_0.5fr_0.5fr_0.5fr_0.5fr_0.5fr_0.5fr] gap-2 mb-2 text-[11px] text-[#8E8E93] font-medium border-b border-[#E9E9E9] pb-2">
                <div>Item</div>
                <div className="text-right">Qty</div>
                <div></div>
                <div>Unit</div>
                <div className="text-right"></div>
                {/* <div className="text-right">Oil</div> */}
                <div className="text-right">Cal</div>
                <div className="text-right">GL</div>
                <div className="text-right">Carb</div>
                <div className="text-right">Prot</div>
                <div className="text-right">Fat</div>
              </div>
              {filledDishes.map((dish) => (
                <div
                  key={dish.id}
                  className="grid grid-cols-[2.8fr_0.6fr_0.3fr_0.9fr_0.5fr_0.5fr_0.5fr_0.5fr_0.5fr_0.5fr] gap-2 mb-1.5 text-[12px] text-[#1F1F1F]"
                >
                  <div className="truncate">{dish.name}</div>
                  <div className="text-right">{dish.quantity}</div>
                  <div></div>
                  <div className="text-[#646464]">{dish.unit}</div>
                  <div className="text-right"></div>
                  {/* <div className="text-right">{dish.oil.toFixed(0)}</div> */}
                  <div className="text-right">{dish.cal.toFixed(0)}</div>
                  <div className="text-right">{dish.gl.toFixed(0)}</div>
                  <div className="text-right">{dish.carb.toFixed(0)}</div>
                  <div className="text-right">{dish.protein.toFixed(0)}</div>
                  <div className="text-right">{dish.fat.toFixed(0)}</div>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ─── Main Modal ──────────────────────────────────────────────────────────────

interface MealPlanPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after the API responds with success: "1" */
  onSendToClient: () => void;
  mealTimings: MealTiming[];
  mealOptions: MealOption[];
  nutritionData: NutritionData | null;
  /** The client's user ID sent to the API */
  userId: string | number;
  /** The meal plan ID sent to the API */
  mealPlanId: string | number;
  /** Defaults to "Regular" */
  mealPlanType?: string;
  /** OneSignal player ID of the client to send push notification */
  playerId?: string;
}

// ─── Send status type ────────────────────────────────────────────────────────

type SendStatus = "idle" | "sending" | "success" | "error";

const MealPlanPreviewModal: React.FC<MealPlanPreviewModalProps> = ({
  isOpen,
  onClose,
  onSendToClient,
  mealTimings,
  mealOptions,
  nutritionData,
  userId,
  mealPlanId,
  mealPlanType = "Regular",
  playerId,
}) => {
  useCustomScrollbar();
  const modalRef = useRef<HTMLDivElement>(null);
  const [sendStatus, setSendStatus] = useState<SendStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Track which meal timing sections are collapsed (hidden by default)
  const [collapsedTimings, setCollapsedTimings] = useState<Set<string>>(
    new Set(),
  );

  // Reset state whenever the modal opens - all sections collapsed by default
  useEffect(() => {
    if (isOpen) {
      setSendStatus("idle");
      setErrorMessage("");
      // Collapse all timing sections when modal opens
      setCollapsedTimings(new Set(mealTimings.map((t) => t.id)));
    }
  }, [isOpen, mealTimings]);

  // Click-outside to close (disabled while sending)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        sendStatus !== "sending" &&
        modalRef.current &&
        !modalRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose, sendStatus]);

  // ESC key to close (disabled while sending)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && sendStatus !== "sending") onClose();
    };
    if (isOpen) document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, sendStatus]);

  if (!isOpen) return null;

  // Toggle collapse state for a timing section
  const toggleTimingCollapse = (timingId: string) => {
    setCollapsedTimings((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(timingId)) {
        newSet.delete(timingId);
      } else {
        newSet.add(timingId);
      }
      return newSet;
    });
  };

  // ── OneSignal notification ────────────────────────────────────────────────
  const sendOneSignalNotification = async (pid: string) => {
    try {
      const response = await fetch('https://api.onesignal.com/notifications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': process.env.REACT_APP_ONESIGNAL_API_KEY as string,
        },
        body: JSON.stringify({
          app_id: process.env.REACT_APP_ONESIGNAL_APP_ID,
          include_player_ids: [pid],
          headings: { en: '👋 Hey! Your Meal Plan is Here' },
          contents: {
            en: "Your meal plan is live 🎉 Let's get started!",
          },
          data: { type: 'meal_plan' },
        }),
      });

      if (!response.ok) {
        console.error('Failed to send OneSignal notification:', response.status);
      }
    } catch (err) {
      console.error('Error sending OneSignal notification:', err);
    }
  };

  // ── API call ─────────────────────────────────────────────────────────────
  const handleSendToClient = async () => {
    if (sendStatus === "sending") return;

    setSendStatus("sending");
    setErrorMessage("");

    try {
      const data = await sendMealPlanToClient({
        user_id: Number(userId),
        meal_plan_id: Number(mealPlanId),
        meal_plan_type: mealPlanType,
        status: "2",
      });

      if (data?.success === "1") {
        setSendStatus("success");
        if (playerId) {
          sendOneSignalNotification(playerId);
        }
        // Give the user a brief moment to see the success state, then propagate
        setTimeout(() => {
          onSendToClient();
        }, 800);
      } else {
        throw new Error(data?.message || "Unexpected response from server.");
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.";
      setErrorMessage(message);
      setSendStatus("error");
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────

  const getTimeInMinutes = (meal: MealTiming) => {
    let hour = parseInt(meal.time.hour, 10);
    const minute = parseInt(meal.time.minute, 10);
    if (meal.time.period === "PM" && hour !== 12) hour += 12;
    if (meal.time.period === "AM" && hour === 12) hour = 0;
    return hour * 60 + minute;
  };

  const sortedTimings = [...mealTimings].sort(
    (a, b) => getTimeInMinutes(a) - getTimeInMinutes(b),
  );

  const grandTotals = nutritionData
    ? {
        cal: nutritionData.current_cal,
        carb: nutritionData.current_carbs,
        fat: nutritionData.current_fats,
        protein: nutritionData.current_protein,
        gl: nutritionData.current_gl,
      }
    : { cal: 0, carb: 0, fat: 0, protein: 0, gl: 0 };

  const grandCPF =
    nutritionData?.current_CPF ||
    calcCPF(
      grandTotals.carb,
      grandTotals.protein,
      grandTotals.fat,
      grandTotals.cal,
    );

  const mealTotals = (timingId: string) => {
    const opts = mealOptions.filter((o) => o.mealTimingId === timingId);
    if (opts.length === 0)
      return { cal: 0, carb: 0, fat: 0, protein: 0, gl: 0 };

    const sum = opts.reduce(
      (acc, opt) => ({
        cal: acc.cal + opt.totals.cal,
        carb: acc.carb + opt.totals.carb,
        fat: acc.fat + opt.totals.fat,
        protein: acc.protein + opt.totals.protein,
        gl: acc.gl + opt.totals.gl,
      }),
      { cal: 0, carb: 0, fat: 0, protein: 0, gl: 0 },
    );

    return {
      cal: sum.cal / opts.length,
      carb: sum.carb / opts.length,
      fat: sum.fat / opts.length,
      protein: sum.protein / opts.length,
      gl: sum.gl / opts.length,
    };
  };

  const formatTime = (t: MealTiming["time"]) =>
    `${t.hour}:${t.minute}${t.period.toLowerCase()}`;

  const isSending = sendStatus === "sending";
  const isSuccess = sendStatus === "success";

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-white rounded-md w-full max-w-6xl max-h-[80vh] overflow-hidden flex flex-col shadow-xl"
      >
        {/* ── Fixed header ───────────────────────────────────────────── */}
        <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-[#E5E5EA]">
          <h2 className="text-[18px] font-semibold text-[#1F1F1F] font-inter">
            Meal plan preview
          </h2>
        </div>

        {/* ── Fixed nutrition bar ────────────────────────────────────── */}
        {nutritionData && (
          <div className="flex-shrink-0 px-6 pt-4 pb-0">
            <div className="flex flex-wrap items-center justify-between gap-6 mb-4">
              <span className="text-sm text-[#1F1F1F] font-inter whitespace-nowrap">
                Cal -{" "}
                <span className="font-semibold text-[#B44AE6]">
                  {Math.round(nutritionData.current_cal)}/
                  {Math.round(nutritionData.total_cals_max)}
                </span>
              </span>
              <span className="text-sm text-[#1F1F1F] font-inter whitespace-nowrap">
                Carb -{" "}
                <span className="font-semibold text-[#FFB532]">
                  {Math.round(nutritionData.current_carbs)}/
                  {Math.round(nutritionData.carbs_max)}g
                </span>
              </span>
              <span className="text-sm text-[#1F1F1F] font-inter whitespace-nowrap">
                Fats -{" "}
                <span className="font-semibold text-[#F25BEC]">
                  {Math.round(nutritionData.current_fats)}/
                  {Math.round(nutritionData.fat_max)}g
                </span>
              </span>
              <span className="text-sm text-[#1F1F1F] font-inter whitespace-nowrap">
                A.pro -{" "}
                <span className="font-semibold text-[#6BBDFA]">
                  {Math.round(nutritionData.current_protein)}/
                  {Math.round(nutritionData.protein_max)}g
                </span>
              </span>
              <span className="text-sm text-[#1F1F1F] font-inter whitespace-nowrap">
                GL -{" "}
                <span className="font-semibold text-[#A0CA07]">
                  {Math.round(nutritionData.current_gl)}/
                  {Math.round(nutritionData.GL_max)}
                </span>
              </span>
              <span className="text-sm text-[#1F1F1F] font-inter whitespace-nowrap">
                C:P:F{" "}
                <span className="font-semibold text-[#007AFF]">
                  {nutritionData.current_CPF}/{nutritionData.CPF_max}
                </span>
              </span>
            </div>
          </div>
        )}

        {/* ── Table area ────────────────────────────────────────────── */}
        <div className="flex-1 min-h-0 px-6 pb-4 flex flex-col">
          <div className="border border-[#E5E5EA] rounded-md overflow-hidden flex flex-col flex-1 min-h-0 relative">
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-overlay">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 z-10 bg-white border-b border-[#E5E5EA]">
                  <tr>
                    <th className="py-2.5 pl-4 pr-3 text-left text-[12px] font-medium text-[#646464] font-inter">
                      Meal
                    </th>
                    <th className="py-2.5 px-3 text-right text-[12px] font-medium text-[#646464] font-inter">
                      Cal
                    </th>
                    <th className="py-2.5 px-3 text-right text-[12px] font-medium text-[#646464] font-inter">
                      Carb
                    </th>
                    <th className="py-2.5 px-3 text-right text-[12px] font-medium text-[#646464] font-inter">
                      A.pro
                    </th>
                    <th className="py-2.5 px-3 text-right text-[12px] font-medium text-[#646464] font-inter">
                      GL
                    </th>
                    <th className="py-2.5 px-3 text-right text-[12px] font-medium text-[#646464] font-inter">
                      Fats
                    </th>
                    <th className="py-2.5 pl-3 pr-4 text-right text-[12px] font-medium text-[#646464] font-inter">
                      C:P:F
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {sortedTimings.map((timing) => {
                    const opts = mealOptions.filter(
                      (o) => o.mealTimingId === timing.id,
                    );
                    const totals = mealTotals(timing.id);
                    const cpf = calcCPF(
                      totals.carb,
                      totals.protein,
                      totals.fat,
                      totals.cal,
                    );
                    const isCollapsed = collapsedTimings.has(timing.id);

                    return (
                      <React.Fragment key={timing.id}>
                        <tr
                          className="bg-[#F9FEFE] hover:bg-[#F9FEFE] transition-colors cursor-pointer"
                          onClick={() => toggleTimingCollapse(timing.id)}
                        >
                          <td className="py-2.5 pl-4 pr-3 pb-3">
                            <div className="flex items-center gap-2">
                              <button
                                className="text-[#8E8E93] hover:text-[#0DB5B5] transition-colors flex-shrink-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleTimingCollapse(timing.id);
                                }}
                              >
                                {isCollapsed ? (
                                  // Plus icon
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M8 3.5V12.5M3.5 8H12.5"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                ) : (
                                  // Minus icon
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M3.5 8H12.5"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </button>
                              <span className="text-[12px] 3xl:text-sm font-medium text-[#1F1F1F] font-inter">
                                {timing.mealType}{" "}
                                <span className="text-[#8E8E93] font-normal">
                                  ({formatTime(timing.time)})
                                </span>
                              </span>
                            </div>
                          </td>
                          <td className="py-2.5 px-3 pb-3 text-sm font-semibold text-[#1F1F1F] text-right">
                            {Math.round(totals.cal)}
                          </td>
                          <td className="py-2.5 px-3 pb-3 text-sm font-semibold text-[#1F1F1F] text-right">
                            {Math.round(totals.carb)}g
                          </td>
                          <td className="py-2.5 px-3 pb-3 text-sm font-semibold text-[#1F1F1F] text-right">
                            {Math.round(totals.protein)}g
                          </td>
                          <td className="py-2.5 px-3 pb-3 text-sm font-semibold text-[#1F1F1F] text-right">
                            {Math.round(totals.gl)}g
                          </td>
                          <td className="py-2.5 px-3 pb-3 text-sm font-semibold text-[#1F1F1F] text-right">
                            {Math.round(totals.fat)}g
                          </td>
                          <td className="py-2.5 pl-3 pr-4 pb-3 text-sm font-semibold text-[#1F1F1F] text-right">
                            {cpf}
                          </td>
                        </tr>

                        {/* Show options only when not collapsed */}
                        {!isCollapsed && (
                          <>
                            {opts.map((option) => (
                              <OptionRow key={option.id} option={option} />
                            ))}

                            {opts.length === 0 && (
                              <tr className="border-b border-[#F3F4F6]">
                                <td
                                  colSpan={7}
                                  className="py-3 pl-8 text-sm text-[#AEAEB2] italic"
                                >
                                  No options added yet
                                </td>
                              </tr>
                            )}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}

                  <tr className="bg-[#F1FBFB] sticky bottom-0 z-10 border-t border-[#CECECE]">
                    <td className="py-3 pl-4 pr-3 text-sm font-semibold text-[#1F1F1F] font-inter">
                      Total
                    </td>
                    <td className="py-3 px-3 text-sm font-semibold text-[#0DB5B5] text-right">
                      {Math.round(grandTotals.cal)}
                    </td>
                    <td className="py-3 px-3 text-sm font-semibold text-[#0DB5B5] text-right">
                      {Math.round(grandTotals.carb)}
                    </td>
                    <td className="py-3 px-3 text-sm font-semibold text-[#0DB5B5] text-right">
                      {Math.round(grandTotals.protein)}
                    </td>
                    <td className="py-3 px-3 text-sm font-semibold text-[#0DB5B5] text-right">
                      {Math.round(grandTotals.gl)}
                    </td>
                    <td className="py-3 px-3 text-sm font-semibold text-[#0DB5B5] text-right">
                      {Math.round(grandTotals.fat)}
                    </td>
                    <td className="py-3 pl-3 pr-4 text-sm font-semibold text-[#0DB5B5] text-right">
                      {grandCPF}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── Footer ─────────────────────────────────────────────────── */}
        <div className="border-t border-[#E5E5EA] px-6 py-4 flex-shrink-0">
          {/* Inline error banner */}
          {sendStatus === "error" && errorMessage && (
            <p className="text-[12px] text-[#E53E3E] mb-3 text-right">
              {errorMessage}
            </p>
          )}

          <div className="flex justify-end items-center gap-3">
            <Button
              variant="secondary"
              size="md"
              onClick={onClose}
              disabled={isSending}
            >
              Close
            </Button>

            <button
              onClick={handleSendToClient}
              disabled={isSending || isSuccess}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 disabled:cursor-not-allowed
                ${
                  isSuccess
                    ? "bg-[#22C55E] text-white opacity-90"
                    : "bg-[#0DB5B5] hover:bg-[#0CA0A0] text-white disabled:opacity-50"
                }`}
            >
              {isSending ? (
                <>
                  {/* Spinner */}
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Sending...
                </>
              ) : isSuccess ? (
                <>
                  {/* Checkmark */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-3-3a1 1 0 011.414-1.414L9 11.586l6.293-6.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Sent!
                </>
              ) : (
                "Send to client"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MealPlanPreviewModal;
